import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Stack, CircularProgress, Alert, Chip, Tooltip,
  ToggleButton, ToggleButtonGroup, TextField, Collapse, Button,
} from '@mui/material';
import AltRouteRounded from '@mui/icons-material/AltRouteRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import CalendarViewWeekRounded from '@mui/icons-material/CalendarViewWeek';

import {
  getRunReadiness, listPressConfig, ReadinessRope, ReadinessRun,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';

/**
 * RunReadinessView — the Drum-Buffer-Rope lens.
 *
 * The press schedule is the drum. Every row below is a *rope*: a deadline
 * derived backwards from a run's `planned_start` by that stage's lead time.
 * Nothing here is scheduled independently — move a job on the Planning Board
 * and every deadline on this screen moves with it, because they are all
 * projections of the same cascade.
 *
 * Deliberately honest about what it cannot see. Only the ink rope has a real
 * supply feed today; imposition is proxied off the artwork cache, imaging knows
 * the plate count but not whether plates exist, and board is timing only. Each
 * says so rather than showing a green tick it has not earned.
 */

const STAGE_LABEL: Record<ReadinessRope['stage'], string> = {
  impose: 'Impose',
  image: 'Image plates',
  ink: 'Ink',
  board: 'Board to press',
};

const STAGE_OWNER: Record<ReadinessRope['stage'], string> = {
  impose: 'pre-press',
  image: 'plate room',
  ink: 'ink kitchen',
  board: 'warehouse',
};

const STATUS_STYLE: Record<ReadinessRope['status'], { fg: string; bg: string; label: string }> = {
  ready:   { fg: TOKENS.good, bg: `${TOKENS.good}18`, label: 'READY' },
  blocked: { fg: TOKENS.bad,  bg: `${TOKENS.bad}14`,  label: 'BLOCKED' },
  late:    { fg: TOKENS.bad,  bg: `${TOKENS.bad}14`,  label: 'LATE' },
  due:     { fg: '#92400e',   bg: `${TOKENS.warn}22`, label: 'DUE' },
  pending: { fg: TOKENS.ink3, bg: TOKENS.lineSoft,    label: 'PENDING' },
  unknown: { fg: TOKENS.ink3, bg: TOKENS.lineSoft,    label: 'TIMING ONLY' },
};

const StatusIcon = ({ status }: { status: ReadinessRope['status'] }) => {
  const c = STATUS_STYLE[status].fg;
  if (status === 'ready') return <CheckCircleRounded sx={{ fontSize: 17, color: c }} />;
  if (status === 'blocked' || status === 'late') return <ErrorOutlineRounded sx={{ fontSize: 17, color: c }} />;
  if (status === 'unknown') return <HelpOutlineRounded sx={{ fontSize: 17, color: c }} />;
  return <ScheduleRounded sx={{ fontSize: 17, color: c }} />;
};

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** "in 14 h" / "6 h ago" — the buffer, in the unit people argue about it in. */
function fmtLeft(hours: number | null): string {
  if (hours == null) return '';
  const abs = Math.abs(hours);
  const unit = abs >= 48 ? `${Math.round(abs / 24)} d` : `${Math.round(abs)} h`;
  return hours < 0 ? `${unit} ago` : `in ${unit}`;
}

export default function RunReadinessView() {
  const navigate = useNavigate();
  const [press, setPress] = useState<string>('');
  const [showLeads, setShowLeads] = useState(false);
  const [leads, setLeads] = useState<Record<string, number>>({});

  const pressQ = useQuery({
    queryKey: productionKeys.pressConfig(), queryFn: listPressConfig, staleTime: 5 * 60_000,
  });

  const q = useQuery({
    queryKey: productionKeys.runReadiness(press || undefined, leads),
    queryFn: () => getRunReadiness({ press: press || undefined, leads }),
    staleTime: 30_000,
  });

  const effectiveLeads = q.data?.leads_hours ?? {};
  const counts = useMemo(() => {
    const c = { blocked: 0, late: 0, due: 0, ok: 0 };
    (q.data?.runs ?? []).forEach((r) => {
      if (r.worst_status === 'blocked') c.blocked += 1;
      else if (r.worst_status === 'late') c.late += 1;
      else if (r.worst_status === 'due') c.due += 1;
      else c.ok += 1;
    });
    return c;
  }, [q.data?.runs]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1180, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
        <AltRouteRounded sx={{ color: TOKENS.accent }} />
        <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Run readiness</Typography>
      </Stack>
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2, maxWidth: 880, lineHeight: 1.6 }}>
        The press is the drum. Every deadline below is pulled backwards from a run’s planned
        start by that stage’s lead time — so this screen schedules nothing. Reorder the
        Planning Board and every date here moves with it.
      </Typography>

      {/* The lead times ARE the buffer, and they are currently invented. Say so
          before anyone reads a deadline as a commitment. */}
      <Alert severity="warning" icon={<TuneRounded />} sx={{ mb: 2 }}
        action={(
          <Button size="small" color="inherit" onClick={() => setShowLeads((v) => !v)}>
            {showLeads ? 'Hide' : 'Adjust'}
          </Button>
        )}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          These lead times are placeholders — nobody has committed to them
        </Typography>
        <Typography sx={{ fontSize: 12.5, mt: 0.25 }}>
          Impose {effectiveLeads.impose}h · Image {effectiveLeads.image}h ·
          Ink {effectiveLeads.ink}h · Board {effectiveLeads.board}h. Each section agreeing a
          lead it can actually hold is the non-software half of this; the software just
          encodes it.
        </Typography>
        <Collapse in={showLeads}>
          <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            {(['impose', 'image', 'ink', 'board'] as const).map((k) => (
              <TextField key={k} size="small" label={`${STAGE_LABEL[k]} (h)`} type="number"
                value={leads[k] ?? effectiveLeads[k] ?? ''}
                onChange={(e) => setLeads((l) => ({ ...l, [k]: Number(e.target.value) }))}
                sx={{ width: 150 }} inputProps={{ min: 0, step: 1 }} />
            ))}
            <Button size="small" onClick={() => setLeads({})} sx={{ color: TOKENS.ink3 }}>
              Reset
            </Button>
          </Stack>
        </Collapse>
      </Alert>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={press}
          onChange={(_e, v) => setPress(v ?? '')}>
          <ToggleButton value="">All presses</ToggleButton>
          {(pressQ.data ?? []).map((p) => (
            <ToggleButton key={p.press_code} value={p.press_code}>{p.press_code}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {counts.blocked > 0 && <Chip size="small" label={`${counts.blocked} blocked`}
          sx={{ fontWeight: 800, color: TOKENS.bad, bgcolor: `${TOKENS.bad}14` }} />}
        {counts.late > 0 && <Chip size="small" label={`${counts.late} late`}
          sx={{ fontWeight: 800, color: TOKENS.bad, bgcolor: `${TOKENS.bad}14` }} />}
        {counts.due > 0 && <Chip size="small" label={`${counts.due} due`}
          sx={{ fontWeight: 800, color: '#92400e', bgcolor: `${TOKENS.warn}22` }} />}
        <Button size="small" startIcon={<CalendarViewWeekRounded />}
          onClick={() => navigate('/production/board')} sx={{ color: TOKENS.accent }}>
          Planning board
        </Button>
      </Stack>

      {q.isLoading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      ) : q.isError ? (
        <Alert severity="error">Couldn’t load readiness.</Alert>
      ) : (q.data?.runs ?? []).length === 0 ? (
        <Box sx={{ border: `1px dashed ${TOKENS.line}`, borderRadius: '12px', p: 5,
          textAlign: 'center', color: TOKENS.ink3 }}>
          <Typography sx={{ fontSize: 13.5 }}>
            Nothing scheduled{press ? ` on ${press}` : ''} — put a job on a press lane and its
            upstream deadlines appear here.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {(q.data?.runs ?? []).map((run) => <RunCard key={run.run_key} run={run} />)}
        </Stack>
      )}
    </Box>
  );
}

function RunCard({ run }: { run: ReadinessRun }) {
  const worst = STATUS_STYLE[run.worst_status];
  return (
    <Box sx={{
      border: `1px solid ${TOKENS.line}`, borderLeft: `5px solid ${worst.fg}`,
      borderRadius: '12px', bgcolor: TOKENS.panel, boxShadow: TOKENS.shadowSm, overflow: 'hidden',
    }}>
      {/* ---- the drum: when this run starts ---- */}
      <Stack direction="row" alignItems="baseline" spacing={1.25}
        sx={{ px: 2, py: 1.25, bgcolor: TOKENS.lineSoft, flexWrap: 'wrap', gap: 0.75 }}>
        <Chip size="small" label={run.press_code}
          sx={{ height: 20, fontWeight: 800, fontFamily: TOKENS.monoFont,
            color: TOKENS.accentStrong, bgcolor: `${TOKENS.accent}1F` }} />
        <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>
          {run.customer_code} · forme {run.forme_number ?? '—'}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
          {run.job_numbers.join(', ')} · {run.total_sheets.toLocaleString()} sheets
          {run.passes > 1 ? ` · ${run.passes} passes` : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>runs</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: TOKENS.accent }}>
          {fmtWhen(run.planned_start)}
        </Typography>
      </Stack>

      {/* ---- the ropes ---- */}
      {run.ropes.map((rope, i) => {
        const st = STATUS_STYLE[rope.status];
        return (
          <Stack key={rope.stage} direction="row" alignItems="flex-start" spacing={1.5}
            sx={{
              px: 2, py: 1.25,
              borderTop: i === 0 ? 'none' : `1px solid ${TOKENS.lineSoft}`,
              bgcolor: rope.status === 'blocked' || rope.status === 'late' ? `${TOKENS.bad}07` : undefined,
            }}>
            <Box sx={{ width: 22, pt: '1px' }}><StatusIcon status={rope.status} /></Box>

            <Box sx={{ width: 132, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{STAGE_LABEL[rope.stage]}</Typography>
              <Typography sx={{ fontSize: 10.5, color: TOKENS.ink3, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {STAGE_OWNER[rope.stage]}
              </Typography>
            </Box>

            <Box sx={{ width: 190, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                by {fmtWhen(rope.ready_by)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: (rope.hours_left ?? 0) < 0 ? TOKENS.bad : TOKENS.ink3 }}>
                {fmtLeft(rope.hours_left)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>{rope.headline}</Typography>
                {rope.proxy && (
                  <Tooltip title="Inferred from something adjacent, not observed directly.">
                    <Chip size="small" label="PROXY"
                      sx={{ height: 15, fontSize: 8.5, fontWeight: 800, letterSpacing: 0.3,
                        color: TOKENS.ink3, bgcolor: TOKENS.lineSoft, cursor: 'help' }} />
                  </Tooltip>
                )}
              </Stack>
              {rope.detail && (
                <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, lineHeight: 1.5, mt: 0.25 }}>
                  {rope.detail}
                </Typography>
              )}
            </Box>

            <Chip size="small" label={st.label}
              sx={{ height: 19, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4,
                color: st.fg, bgcolor: st.bg, flexShrink: 0 }} />
          </Stack>
        );
      })}
    </Box>
  );
}
